import React, { useState } from 'react';
import { Dropdown, ButtonGroup, Spinner } from 'react-bootstrap';
import { FaFilePdf, FaFileExcel, FaDownload } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'react-toastify';
import QRCode from 'qrcode';
import logoImg from '../../images/logo.png';

const ExportUtility = ({
  fetchData,
  columns,
  filename = 'export',
  pdfTitle = 'Report',
  templateData = null,
  templateCols = null,
  orientation = 'portrait',
  disableExcel = false,
  pdfOnlyButton = false,
  excelOnlyButton = false,
  pdfButtonText = 'Export PDF',
  excelButtonText = 'Export Excel',
  pdfSubtitle = '',
  showPrintDate = true,
  onPdfClick = null,           // optional: override internal PDF generation
  isExternalLoading = false,   // optional: spinner state from parent
  btnClassName = '',           // optional: override button class
  btnStyle = null,             // optional: override button inline styles
}) => {
  const [isExporting, setIsExporting] = useState(false);

  const loadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => resolve(img);
      img.onerror = (err) => reject(err);
      img.src = src;
    });
  };

  // Generate a QR code PNG data URL using the headless `qrcode` library
  const generateQRDataUrl = (text, color = '#00a1ff') => {
    return QRCode.toDataURL(text, {
      errorCorrectionLevel: 'H',
      width: 200,
      margin: 1,
      color: {
        dark: color,   // QR modules colour
        light: '#ffffff'
      }
    });
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const result = await fetchData();
      if (!result) {
        toast.warning('Hakuna taarifa za kuexport (No data to export)');
        return;
      }

      let data = result;
      let titleToUse = pdfTitle;
      let subtitleToUse = pdfSubtitle;

      if (result && !Array.isArray(result) && Array.isArray(result.data)) {
        data = result.data;
        if (result.pdfTitle) titleToUse = result.pdfTitle;
        if (result.pdfSubtitle) subtitleToUse = result.pdfSubtitle;
      }

      if (data.length === 0) {
        toast.warning('Hakuna taarifa za kuexport (No data to export)');
        return;
      }

      const doc = new jsPDF({ orientation });
      let logo = null;
      let qrDataUrl = null;
      try {
        logo = await loadImage(logoImg);
      } catch (err) {
        console.warn('Could not load logo watermark', err);
      }
      try {
        qrDataUrl = await generateQRDataUrl('https://harusionline.co.tz/Home', '#00a1ff');
      } catch (err) {
        console.warn('Could not generate QR code', err);
      }

      // Title
      doc.setFont('courier');
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      const pageWidth = doc.internal.pageSize.getWidth();
      doc.text(titleToUse, pageWidth / 2, 12, { align: 'center' });

      // Subtitle (e.g. Totals)
      if (subtitleToUse) {
        doc.setFontSize(8);
        doc.text(subtitleToUse, pageWidth / 2, 17, { align: 'center' });
      }

      const tableColumn = columns.map(col => col.header);
      const tableRows = data.map(row => columns.map(col => row[col.dataKey] !== undefined && row[col.dataKey] !== null ? row[col.dataKey].toString() : ''));

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: pdfSubtitle ? 22 : 18,
        theme: 'grid',
        styles: {
          font: 'courier',
          fontStyle: 'bold',
          fontSize: 9,
          cellPadding: 1.5,
          halign: 'center',
          valign: 'middle',
          textColor: [0, 0, 0],
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
        },
        alternateRowStyles: {
          fillColor: [255, 255, 255],
        },
        didDrawPage: function (data) {
          const pageWidth = doc.internal.pageSize.getWidth();
          const pageHeight = doc.internal.pageSize.getHeight();

          // ── Watermark logo ──
          if (logo) {
            const imgWidth = 120;
            const imgHeight = (logo.height * imgWidth) / logo.width;
            const x = (pageWidth - imgWidth) / 2;
            const y = (pageHeight - imgHeight) / 2;
            doc.setGState(new doc.GState({ opacity: 0.1 }));
            doc.addImage(logo, 'PNG', x, y, imgWidth, imgHeight);
            doc.setGState(new doc.GState({ opacity: 1.0 }));
          }

          // ── Footer: QR left | Page centered | Printed right ──
          const footerY = pageHeight - 8;
          const qrSize = 13;
          const qrY = pageHeight - qrSize - 2;

          // QR code – left side (where page number used to be)
          if (qrDataUrl) {
            doc.addImage(qrDataUrl, 'PNG', 6, qrY, qrSize, qrSize);
          }

          if (showPrintDate) {
            doc.setFont('courier');
            doc.setFontSize(7.5);
            doc.setTextColor(120, 120, 120);

            // Page number – centered
            const pageInfo = `Page ${data.pageNumber}`;
            doc.text(pageInfo, pageWidth / 2, footerY, { align: 'center' });

            // Printed date – right
            const now = new Date();
            const dateStr = `Printed: ${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
            doc.text(dateStr, pageWidth - 14, footerY, { align: 'right' });
          }
        }
      });

      doc.save(`${filename}.pdf`);
      toast.success('PDF Downloaded successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Error downloading PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const data = await fetchData();
      if (!data || data.length === 0) {
        toast.warning('No data to export');
        return;
      }

      const exportData = data.map(row => {
        const rowObj = {};
        columns.forEach(col => {
          rowObj[col.header] = row[col.dataKey] !== undefined && row[col.dataKey] !== null ? row[col.dataKey].toString() : '';
        });
        return rowObj;
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, "Data");
      XLSX.writeFile(wb, `${filename}.xlsx`);
      toast.success('Excel downloaded successfully!');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error('Error exporting Excel');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    if (!templateData) return;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(templateData);
    if (templateCols) {
      ws['!cols'] = templateCols;
    }
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, `${filename}_template.xlsx`);
    toast.info('Template imepakuliwa');
  };

  if (pdfOnlyButton) {
    const handleClick = onPdfClick || handleExportPDF;
    const busy = isExternalLoading || (onPdfClick ? false : isExporting);
    return (
      <button
        className={btnClassName || "btn btn-light btn-sm d-flex align-items-center gap-1 border shadow-sm px-3"}
        onClick={handleClick}
        disabled={busy}
        style={btnStyle || { fontSize: '0.8rem', borderRadius: '4px', height: '32px' }}
      >
        {busy ? <Spinner size="sm" animation="border" /> : <FaFilePdf className="text-danger" />}
        {pdfButtonText}
      </button>
    );
  }

  if (excelOnlyButton) {
    const busy = isExternalLoading || isExporting;
    return (
      <button
        className={btnClassName || "btn btn-light btn-sm d-flex align-items-center gap-1 border shadow-sm px-3"}
        onClick={handleExportExcel}
        disabled={busy}
        style={btnStyle || { fontSize: '0.8rem', borderRadius: '4px', height: '32px' }}
      >
        {busy ? <Spinner size="sm" animation="border" /> : <FaFileExcel className="text-success" />}
        {excelButtonText}
      </button>
    );
  }

  return (
    <Dropdown as={ButtonGroup}>
      <Dropdown.Toggle
        variant="outline-primary"
        id="dropdown-export"
        disabled={isExporting}
        className="d-flex align-items-center gap-2"
        style={{ borderRadius: '8px', padding: '8px 16px', fontWeight: '500' }}
      >
        {isExporting ? <Spinner size="sm" animation="border" /> : <FaDownload />}
        Export
      </Dropdown.Toggle>

      <Dropdown.Menu className="shadow-sm border-0 mt-2" style={{ borderRadius: '8px' }}>
        <Dropdown.Item onClick={handleExportPDF} className="d-flex align-items-center gap-3 py-2">
          <FaFilePdf className="text-danger" size={18} /> Export as PDF
        </Dropdown.Item>
        {!disableExcel && (
          <Dropdown.Item onClick={handleExportExcel} className="d-flex align-items-center gap-3 py-2">
            <FaFileExcel className="text-success" size={18} /> Export as Excel
          </Dropdown.Item>
        )}
        {templateData && (
          <>
            <Dropdown.Divider />
            <Dropdown.Item onClick={handleDownloadTemplate} className="d-flex align-items-center gap-3 py-2 text-secondary">
              <FaDownload size={18} /> Download Import Template
            </Dropdown.Item>
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ExportUtility;
